import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Send,
  Plus,
  CalendarPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useViewPreferences } from "@/hooks/use-view-preferences";
import { useCommunicationConnection } from "@/hooks/useCommunicationConnection";
import { useCommunicationAnalyze } from "@/hooks/useCommunicationAnalyze";
import { useIntegrationIndexMap, getIntegrationIndexStatus } from "@/hooks/useIntegrationIndexMap";
import {
  useAddGoHighLevelContactTag,
  useBookGoHighLevelAppointment,
  useCreateGoHighLevelContact,
  useGoHighLevelCalendars,
  useGoHighLevelContacts,
  useGoHighLevelConversations,
  useGoHighLevelMessages,
  useGoHighLevelOpportunities,
  useSendGoHighLevelMessage,
} from "@/hooks/useGoHighLevel";
import { CommunicationInboxLayout } from "@/components/communications/CommunicationInboxLayout";
import { CommunicationMessageList } from "@/components/communications/CommunicationMessageList";
import { CommunicationAgentResultsDialog } from "@/components/communications/CommunicationAgentResultsDialog";
import { IntegrationRagActionBar } from "@/components/integrations/IntegrationRagActionBar";
import { IntegrationIndexBadge } from "@/components/integrations/IntegrationIndexBadge";
import { filterCommunicationItems, sortCommunicationItems } from "@/lib/communications/filterSortMessages";
import {
  goHighLevelCalendarToInboxItem,
  goHighLevelContactToInboxItem,
  goHighLevelConversationToInboxItem,
  goHighLevelOpportunityToInboxItem,
} from "@/lib/gohighlevel/toInboxItem";
import { formatGoHighLevelMarkdown, goHighLevelMetadata } from "@/lib/integrationRag/formatGoHighLevel";
import { goHighLevelService } from "@/services/goHighLevelService";
import {
  integrationRagService,
  saveIntegrationLibraryBackup,
} from "@/services/integrationRagService";
import type { CommunicationInboxItem, CommunicationSortField } from "@/types/communications";
import type { GoHighLevelEntity, GoHighLevelTab } from "@/types/gohighlevel";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;

const TAB_CONFIG: { id: GoHighLevelTab; label: string }[] = [
  { id: "conversations", label: "Conversations" },
  { id: "contacts", label: "Contacts" },
  { id: "opportunities", label: "Opportunities" },
  { id: "calendars", label: "Calendars" },
];

export default function GoHighLevelHub() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<GoHighLevelTab>("conversations");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageToken, setPageToken] = useState<string | undefined>();
  const [selectedIds, setSelectedIds] = useState<Record<string, true>>({});
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [composeText, setComposeText] = useState("");
  const [indexing, setIndexing] = useState(false);
  const [indexProgress, setIndexProgress] = useState<string | null>(null);
  const [saveToLibrary, setSaveToLibrary] = useState(false);

  const [createContactOpen, setCreateContactOpen] = useState(false);
  const [newContact, setNewContact] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [bookOpen, setBookOpen] = useState(false);
  const [bookForm, setBookForm] = useState({ contactId: "", startTime: "", title: "" });

  const { viewMode, setViewMode, sortField, setSortField, sortDirection, setSortDirection } =
    useViewPreferences<CommunicationSortField>({
      storageKey: "deeply.gohighlevel-inbox",
      defaultViewMode: "list",
      defaultSortField: "date",
      defaultSortDirection: "desc",
    });

  const connection = useCommunicationConnection("gohighlevel", user?.id);
  const analyze = useCommunicationAnalyze();
  const indexMap = useIntegrationIndexMap("gohighlevel");
  const sendMutation = useSendGoHighLevelMessage();
  const createContactMutation = useCreateGoHighLevelContact();
  const addTagMutation = useAddGoHighLevelContactTag();
  const bookMutation = useBookGoHighLevelAppointment();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "true") {
      toast({ title: "GoHighLevel connected" });
      void connection.refetchStatus();
      window.history.replaceState({}, "", "/communication/gohighlevel");
    }
    const err = params.get("error");
    if (err) {
      toast({ title: "Connection failed", description: decodeURIComponent(err), variant: "destructive" });
      window.history.replaceState({}, "", "/communication/gohighlevel");
    }
  }, [connection, toast]);

  const listParams = useMemo(
    () => ({ limit: PAGE_SIZE, pageToken, query: searchQuery || undefined }),
    [pageToken, searchQuery]
  );

  const conversationsQuery = useGoHighLevelConversations(user?.id, connection.connected, listParams);
  const contactsQuery = useGoHighLevelContacts(user?.id, connection.connected, listParams);
  const opportunitiesQuery = useGoHighLevelOpportunities(user?.id, connection.connected, listParams);
  const calendarsQuery = useGoHighLevelCalendars(user?.id, connection.connected);
  const messagesQuery = useGoHighLevelMessages(
    activeConversationId,
    connection.connected && activeTab === "conversations",
    { limit: 50 }
  );

  const resetPagination = useCallback(() => {
    setPageToken(undefined);
    setActiveId(null);
    setActiveConversationId(null);
    setSelectedIds({});
  }, []);

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab as GoHighLevelTab);
      resetPagination();
      setSearchQuery("");
    },
    [resetPagination]
  );

  const rawItems = useMemo((): GoHighLevelEntity[] => {
    switch (activeTab) {
      case "conversations":
        return conversationsQuery.data?.conversations ?? [];
      case "contacts":
        return contactsQuery.data?.contacts ?? [];
      case "opportunities":
        return opportunitiesQuery.data?.opportunities ?? [];
      case "calendars":
        return calendarsQuery.data?.calendars ?? [];
      default:
        return [];
    }
  }, [activeTab, conversationsQuery.data, contactsQuery.data, opportunitiesQuery.data, calendarsQuery.data]);

  const inboxItems = useMemo((): CommunicationInboxItem[] => {
    switch (activeTab) {
      case "conversations":
        return rawItems.map((e) => goHighLevelConversationToInboxItem(e));
      case "contacts":
        return rawItems.map((e) => goHighLevelContactToInboxItem(e));
      case "opportunities":
        return rawItems.map((e) => goHighLevelOpportunityToInboxItem(e));
      case "calendars":
        return rawItems.map((e) => goHighLevelCalendarToInboxItem(e));
      default:
        return [];
    }
  }, [activeTab, rawItems]);

  const filteredItems = useMemo(() => {
    const filtered = filterCommunicationItems(inboxItems, searchQuery);
    return sortCommunicationItems(filtered, sortField, sortDirection);
  }, [inboxItems, searchQuery, sortField, sortDirection]);

  const activeEntity = rawItems.find((e) => e.id === activeId);
  const nextPageToken = useMemo(() => {
    switch (activeTab) {
      case "conversations":
        return conversationsQuery.data?.nextPageToken;
      case "contacts":
        return contactsQuery.data?.nextPageToken;
      case "opportunities":
        return opportunitiesQuery.data?.nextPageToken;
      default:
        return undefined;
    }
  }, [activeTab, conversationsQuery.data, contactsQuery.data, opportunitiesQuery.data]);

  const isLoading =
    connection.isLoadingStatus ||
    (activeTab === "conversations" && conversationsQuery.isLoading) ||
    (activeTab === "contacts" && contactsQuery.isLoading) ||
    (activeTab === "opportunities" && opportunitiesQuery.isLoading) ||
    (activeTab === "calendars" && calendarsQuery.isLoading);

  const isFetching =
    conversationsQuery.isFetching ||
    contactsQuery.isFetching ||
    opportunitiesQuery.isFetching ||
    calendarsQuery.isFetching;

  const isError =
    conversationsQuery.isError ||
    contactsQuery.isError ||
    opportunitiesQuery.isError ||
    calendarsQuery.isError;

  const errorMessage = useMemo(() => {
    const err =
      conversationsQuery.error ||
      contactsQuery.error ||
      opportunitiesQuery.error ||
      calendarsQuery.error;
    return err instanceof Error ? err.message : undefined;
  }, [conversationsQuery.error, contactsQuery.error, opportunitiesQuery.error, calendarsQuery.error]);

  const handleRefresh = useCallback(() => {
    void conversationsQuery.refetch();
    void contactsQuery.refetch();
    void opportunitiesQuery.refetch();
    void calendarsQuery.refetch();
    if (activeConversationId) void messagesQuery.refetch();
  }, [conversationsQuery, contactsQuery, opportunitiesQuery, calendarsQuery, messagesQuery, activeConversationId]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev[id]) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: true };
    });
  }, []);

  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedIds({});
        return;
      }
      const next: Record<string, true> = {};
      for (const item of filteredItems) next[item.id] = true;
      setSelectedIds(next);
    },
    [filteredItems]
  );

  const handleSelectItem = useCallback(
    (id: string) => {
      setActiveId(id);
      if (activeTab === "conversations") {
        setActiveConversationId(id);
      }
    },
    [activeTab]
  );

  const handleAnalyze = useCallback(async () => {
    if (!selectedAgentId) return;
    const ids = Object.keys(selectedIds);
    const items = [];
    for (const id of ids) {
      const summary = filteredItems.find((m) => m.id === id);
      const entity = rawItems.find((e) => e.id === id);
      let body = summary?.preview || "";
      if (activeTab === "conversations" && activeConversationId === id && messagesQuery.data?.messages) {
        body = messagesQuery.data.messages.map((m) => `${m.from}: ${m.body || m.preview}`).join("\n");
      } else if (entity) {
        body = formatGoHighLevelMarkdown(entity, activeTab);
      }
      items.push({
        id,
        label: summary?.subject || id,
        subject: summary?.subject || "",
        from: summary?.from || "",
        date: summary?.date,
        body,
      });
    }
    await analyze.runBatchAnalyze(selectedAgentId, items);
  }, [
    selectedAgentId,
    selectedIds,
    filteredItems,
    rawItems,
    activeTab,
    activeConversationId,
    messagesQuery.data,
    analyze,
  ]);

  const handleIndexRaw = useCallback(async () => {
    const ids = Object.keys(selectedIds);
    if (ids.length === 0) return;
    setIndexing(true);
    try {
      const ragItems = [];
      for (const id of ids) {
        const entity = rawItems.find((e) => e.id === id);
        if (!entity) continue;
        let messages;
        if (activeTab === "conversations" && activeConversationId === id) {
          messages = messagesQuery.data?.messages;
        } else if (activeTab === "conversations") {
          try {
            const data = await goHighLevelService.listMessages(id, { limit: 50 });
            messages = data.messages;
          } catch {
            /* use entity only */
          }
        }
        ragItems.push({
          source: "gohighlevel" as const,
          externalId: `${activeTab}:${id}`,
          title: entity.subject,
          content: formatGoHighLevelMarkdown(entity, activeTab, messages),
          metadata: goHighLevelMetadata(entity, activeTab),
          remoteUpdatedAt: entity.date,
        });
      }
      await integrationRagService.indexItems(ragItems, {
        contentMode: "raw",
        onProgress: (done, total) => setIndexProgress(`${done}/${total}`),
      });
      if (saveToLibrary) {
        await saveIntegrationLibraryBackup(ragItems, { includeInRag: false });
      }
      void queryClient.invalidateQueries({ queryKey: ["integration-index", "gohighlevel"] });
      toast({ title: "Indexed for RAG", description: `${ragItems.length} item(s) embedded.` });
    } catch (e: unknown) {
      toast({
        title: "Index failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIndexing(false);
      setIndexProgress(null);
    }
  }, [
    selectedIds,
    rawItems,
    activeTab,
    activeConversationId,
    messagesQuery.data,
    saveToLibrary,
    queryClient,
    toast,
  ]);

  const handleSendMessage = useCallback(async () => {
    if (!activeConversationId || !composeText.trim()) return;
    try {
      await sendMutation.mutateAsync({
        conversationId: activeConversationId,
        body: composeText.trim(),
      });
      setComposeText("");
      toast({ title: "Message sent" });
    } catch (e: unknown) {
      toast({
        title: "Send failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  }, [activeConversationId, composeText, sendMutation, toast]);

  const handleCreateContact = useCallback(async () => {
    try {
      await createContactMutation.mutateAsync(newContact);
      setCreateContactOpen(false);
      setNewContact({ firstName: "", lastName: "", email: "", phone: "" });
      toast({ title: "Contact created" });
    } catch (e: unknown) {
      toast({
        title: "Create failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  }, [createContactMutation, newContact, toast]);

  const handleAddTag = useCallback(async () => {
    if (!activeId || !newTag.trim()) return;
    try {
      await addTagMutation.mutateAsync({ contactId: activeId, tag: newTag.trim() });
      setAddTagOpen(false);
      setNewTag("");
      toast({ title: "Tag added" });
    } catch (e: unknown) {
      toast({
        title: "Tag failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  }, [activeId, newTag, addTagMutation, toast]);

  const handleBookAppointment = useCallback(async () => {
    if (!activeId || !bookForm.contactId || !bookForm.startTime) return;
    try {
      await bookMutation.mutateAsync({
        calendarId: activeId,
        input: {
          contactId: bookForm.contactId,
          startTime: bookForm.startTime,
          title: bookForm.title || "Appointment",
        },
      });
      setBookOpen(false);
      setBookForm({ contactId: "", startTime: "", title: "" });
      toast({ title: "Appointment booked" });
    } catch (e: unknown) {
      toast({
        title: "Booking failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  }, [activeId, bookForm, bookMutation, toast]);

  const handleSortChange = useCallback(
    (field: CommunicationSortField, direction: typeof sortDirection) => {
      setSortField(field);
      setSortDirection(direction);
    },
    [setSortField, setSortDirection]
  );

  const tabDescriptions: Record<GoHighLevelTab, string> = {
    conversations: "SMS, email, and chat threads from your GoHighLevel location",
    contacts: "CRM contacts with tags and contact details",
    opportunities: "Pipeline deals and opportunity stages",
    calendars: "Calendars and appointment booking",
  };

  return (
    <>
      <CommunicationInboxLayout
        icon={<Building2 className="h-6 w-6 text-primary" />}
        title="GoHighLevel"
        description="Conversations, contacts, opportunities, and calendars from your connected location"
        connected={connection.connected}
        connectionLabel={connection.connectionLabel}
        onConnect={() =>
          void connection.connect().catch((e) =>
            toast({ title: "Error", description: e.message, variant: "destructive" })
          )
        }
        onDisconnect={() =>
          void connection.disconnect().catch((e) =>
            toast({ title: "Error", description: e.message, variant: "destructive" })
          )
        }
        isConnecting={connection.isConnecting}
        isDisconnecting={connection.isDisconnecting}
        onRefresh={handleRefresh}
        isRefreshing={isFetching}
        isLoading={isLoading}
        isError={isError}
        errorMessage={errorMessage}
        searchQuery={searchQuery}
        onSearchChange={(q) => {
          setSearchQuery(q);
          setPageToken(undefined);
        }}
        searchPlaceholder={`Search ${activeTab}...`}
        sortField={sortField}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterTabs={TAB_CONFIG}
        activeFilterTab={activeTab}
        onFilterTabChange={handleTabChange}
        integrationScope="gohighlevel"
        integrationToolbar={
          <IntegrationRagActionBar
            selectedCount={Object.keys(selectedIds).length}
            onIndexRaw={() => void handleIndexRaw()}
            onAnalyze={() => void handleAnalyze()}
            showAnalyze={false}
            indexing={indexing}
            indexProgress={indexProgress}
            saveToLibrary={saveToLibrary}
            onSaveToLibraryChange={setSaveToLibrary}
            disabled={analyze.isAnalyzing || indexing}
          />
        }
        selectedAgentId={selectedAgentId}
        onAgentChange={setSelectedAgentId}
        selectedCount={Object.keys(selectedIds).length}
        onAnalyzeSelected={() => void handleAnalyze()}
        isAnalyzing={analyze.isAnalyzing}
        batchProgress={
          analyze.batchProcessing
            ? {
                completed: analyze.batchProcessing.completed,
                total: analyze.batchProcessing.total,
                currentLabel: analyze.batchProcessing.currentLabel,
              }
            : undefined
        }
        disabled={analyze.isAnalyzing || indexing}
        cardTitle={TAB_CONFIG.find((t) => t.id === activeTab)?.label ?? "GoHighLevel"}
        cardDescription={tabDescriptions[activeTab]}
        toolbarEndSlot={
          activeTab === "contacts" ? (
            <Button size="sm" variant="outline" onClick={() => setCreateContactOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> Contact
            </Button>
          ) : activeTab === "calendars" && activeId ? (
            <Button size="sm" variant="outline" onClick={() => setBookOpen(true)}>
              <CalendarPlus className="h-4 w-4 mr-1" /> Book
            </Button>
          ) : null
        }
      >
        <div
          className={cn(
            "grid gap-4",
            activeTab === "conversations" ? "grid-cols-1 lg:grid-cols-5" : "grid-cols-1 lg:grid-cols-5"
          )}
        >
          <div className="lg:col-span-3 space-y-3">
            <CommunicationMessageList
              items={filteredItems}
              viewMode={viewMode}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onSelectAllChange={handleSelectAllChange}
              onSelectItem={handleSelectItem}
              activeId={activeId}
              disabled={analyze.isAnalyzing || indexing}
              renderTrailingBadge={(item) => (
                <IntegrationIndexBadge
                  status={getIntegrationIndexStatus(`${activeTab}:${item.id}`, indexMap)}
                />
              )}
              emptyMessage={
                searchQuery ? `No ${activeTab} match your search` : `No ${activeTab} found`
              }
            />
            {nextPageToken && activeTab !== "calendars" && (
              <div className="flex justify-center gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!pageToken}
                  onClick={() => setPageToken(undefined)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> First
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPageToken(nextPageToken)}>
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            {activeTab === "conversations" && activeConversationId ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{activeEntity?.subject || "Conversation"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {messagesQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading messages...</p>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {(messagesQuery.data?.messages ?? []).map((m) => (
                        <div key={m.id} className="text-sm border-b pb-2">
                          <p className="font-medium text-xs text-muted-foreground">
                            {m.from} · {new Date(m.date).toLocaleString()}
                          </p>
                          <p className="whitespace-pre-wrap">{m.body || m.preview}</p>
                        </div>
                      ))}
                      {(messagesQuery.data?.messages ?? []).length === 0 && (
                        <p className="text-sm text-muted-foreground">No messages in thread</p>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 border-t">
                    <Textarea
                      placeholder="Reply..."
                      value={composeText}
                      onChange={(e) => setComposeText(e.target.value)}
                      rows={2}
                      className="flex-1"
                    />
                    <Button
                      size="icon"
                      onClick={() => void handleSendMessage()}
                      disabled={sendMutation.isPending || !composeText.trim()}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : activeEntity ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{activeEntity.subject}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p>
                    <span className="text-muted-foreground">From:</span> {activeEntity.from}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Date:</span>{" "}
                    {new Date(activeEntity.date).toLocaleString()}
                  </p>
                  {activeTab === "contacts" && (
                    <>
                      {"email" in activeEntity && activeEntity.email && (
                        <p>
                          <span className="text-muted-foreground">Email:</span> {activeEntity.email}
                        </p>
                      )}
                      {"phone" in activeEntity && activeEntity.phone && (
                        <p>
                          <span className="text-muted-foreground">Phone:</span> {activeEntity.phone}
                        </p>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setAddTagOpen(true)}>
                        Add tag
                      </Button>
                    </>
                  )}
                  {activeTab === "opportunities" && "monetaryValue" in activeEntity && (
                    <p>
                      <span className="text-muted-foreground">Value:</span> $
                      {activeEntity.monetaryValue ?? "—"}
                    </p>
                  )}
                  <div className="pt-2 border-t whitespace-pre-wrap">{activeEntity.preview}</div>
                </CardContent>
              </Card>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                Select an item to view details
              </p>
            )}
          </div>
        </div>
      </CommunicationInboxLayout>

      <CommunicationAgentResultsDialog
        open={analyze.resultsOpen}
        onOpenChange={analyze.setResultsOpen}
        results={analyze.results}
      />

      <Dialog open={createContactOpen} onOpenChange={setCreateContactOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>First name</Label>
              <Input
                value={newContact.firstName}
                onChange={(e) => setNewContact((p) => ({ ...p, firstName: e.target.value }))}
              />
            </div>
            <div>
              <Label>Last name</Label>
              <Input
                value={newContact.lastName}
                onChange={(e) => setNewContact((p) => ({ ...p, lastName: e.target.value }))}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact((p) => ({ ...p, email: e.target.value }))}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={newContact.phone}
                onChange={(e) => setNewContact((p) => ({ ...p, phone: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => void handleCreateContact()} disabled={createContactMutation.isPending}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addTagOpen} onOpenChange={setAddTagOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add tag</DialogTitle>
          </DialogHeader>
          <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Tag name" />
          <DialogFooter>
            <Button onClick={() => void handleAddTag()} disabled={addTagMutation.isPending}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Contact ID</Label>
              <Input
                value={bookForm.contactId}
                onChange={(e) => setBookForm((p) => ({ ...p, contactId: e.target.value }))}
              />
            </div>
            <div>
              <Label>Start time (ISO)</Label>
              <Input
                type="datetime-local"
                value={bookForm.startTime}
                onChange={(e) => setBookForm((p) => ({ ...p, startTime: e.target.value }))}
              />
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={bookForm.title}
                onChange={(e) => setBookForm((p) => ({ ...p, title: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => void handleBookAppointment()} disabled={bookMutation.isPending}>
              Book
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
